import React, { useState, useEffect } from 'react';
import { apiService, ActionObjectRelation, Action, Object } from '../services/api';
import logger from '../utils/logger';

const RelationManager: React.FC = () => {
  const [relations, setRelations] = useState<ActionObjectRelation[]>([]);
  const [actions, setActions] = useState<Action[]>([]);
  const [objects, setObjects] = useState<Object[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newRelation, setNewRelation] = useState({ action_id: 0, object_id: 0 });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [relationsData, actionsData, objectsData] = await Promise.all([
        apiService.getRelations(),
        apiService.getActions(),
        apiService.getObjects()
      ]);
      setRelations(relationsData);
      setActions(actionsData);
      setObjects(objectsData);
    } catch (err) {
      setError('Failed to load data');
      logger.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRelation.action_id || !newRelation.object_id) {
      setError('Please select both an action and an object.');
      return;
    }

    try {
      await apiService.createRelation({
        action_id: newRelation.action_id,
        object_id: newRelation.object_id
      });
      setNewRelation({ action_id: 0, object_id: 0 });
      setSuccess('Relation created successfully!');
      await loadData();
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      if (err.response?.status === 409) {
        setError('This relation already exists.');
      } else {
        setError('Failed to create relation');
      }
      logger.error('Create error:', err);
    }
  };

  const handleDelete = async (action_id: number, object_id: number) => {
    if (!window.confirm('Are you sure you want to delete this relation?')) return;

    try {
      await apiService.deleteRelation(action_id, object_id);
      setSuccess('Relation deleted successfully!');
      await loadData();
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to delete relation');
      logger.error('Delete error:', err);
    }
  };

  const handleCSVUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const result = await apiService.uploadRelationsCSV(file);
      setSuccess(`Successfully processed ${result.processed} relations, inserted ${result.inserted} new relations`);
      await loadData();
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      logger.error('Upload error:', err);
      
      // Enhanced error handling
      if (err.response?.status === 401) {
        setError('Authentication failed. Please check your API key.');
      } else if (err.response?.status === 403) {
        setError('Access denied. You do not have permission to upload CSV files.');
      } else if (err.response?.data?.error) {
        // Server returned a specific error message
        const errorMessage = err.response.data.error;
        const errorDetails = err.response.data.details;
        setError(errorDetails ? `${errorMessage}\n${errorDetails}` : errorMessage);
      } else if (err.code === 'NETWORK_ERROR' || err.message?.includes('Network Error')) {
        setError('Network error: Unable to connect to server. Please check if the backend is running.');
      } else {
        setError(`Failed to upload CSV: ${err.message || 'Unknown error'}`);
      }
    } finally {
      e.target.value = '';
    }
  };

  const handleCSVDownload = async () => {
    try {
      const blob = await apiService.downloadRelationsCSV();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'action_object_relations.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download CSV');
      logger.error('Download error:', err);
    }
  };

  // Get action name by ID
  const getActionName = (actionId: number): string => {
    const action = actions.find(a => a.id === actionId);
    return action ? action.actionName : `Action #${actionId}`;
  };

  // Get object name by ID
  const getObjectName = (objectId: number): string => {
    const object = objects.find(o => o.id === objectId);
    return object ? object.name : `Object #${objectId}`;
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading relations...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Action-Object Relations</h1>
        <p className="text-gray-600">
          Manage relationships between actions and objects. Create connections that define which actions can be performed on which objects.
        </p>
      </div>

      {/* Add New Relation */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Add New Relation</h2>
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Action *
              </label>
              <select
                value={newRelation.action_id}
                onChange={(e) => setNewRelation({ ...newRelation, action_id: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value={0}>Select an action...</option>
                {actions.map((action) => (
                  <option key={action.id} value={action.id}>
                    {action.actionName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Object *
              </label>
              <select
                value={newRelation.object_id}
                onChange={(e) => setNewRelation({ ...newRelation, object_id: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value={0}>Select an object...</option>
                {objects.map((object) => (
                  <option key={object.id} value={object.id}>
                    {object.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Create Relation
          </button>
        </form>
      </div>

      {/* CSV Operations */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Bulk Operations</h3>
        <div className="flex flex-wrap gap-4">
          <div>
            <input
              type="file"
              accept=".csv"
              onChange={handleCSVUpload}
              className="hidden"
              id="csv-upload"
            />
            <label
              htmlFor="csv-upload"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 cursor-pointer"
            >
              Upload CSV
            </label>
          </div>
          <button
            onClick={handleCSVDownload}
            className="px-4 py-2 bg-purple-600 text-white font-medium rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          >
            Download CSV
          </button>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          CSV format: action_id,object_id (header row required). Both action_id and object_id must be valid numbers referencing existing Actions and Objects.
        </p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Success</h3>
              <div className="mt-2 text-sm text-green-700">{success}</div>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700 whitespace-pre-line">{error}</div>
              <button
                onClick={() => setError(null)}
                className="mt-2 text-sm text-red-600 underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Relations List */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Relations ({relations.length})
        </h3>

        {relations.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No relations found. Create some relations to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Object ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Object Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {relations.map((relation) => (
                  <tr key={`${relation.action_id}-${relation.object_id}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      #{relation.action_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">
                        {relation.action_name || getActionName(relation.action_id)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      #{relation.object_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">
                        {relation.object_name || getObjectName(relation.object_id)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {relation.created_at ? new Date(relation.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleDelete(relation.action_id, relation.object_id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Statistics */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Statistics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{actions.length}</div>
            <div className="text-sm text-gray-500">Total Actions</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{objects.length}</div>
            <div className="text-sm text-gray-500">Total Objects</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">{relations.length}</div>
            <div className="text-sm text-gray-500">Total Relations</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RelationManager;